import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import distributorService from '../services/distributorService';
import useShippingRequest from '../hooks/useShippingRequest';
import { useLocale, useT } from '../i18n/LanguageContext';
import '../styles/distributor-workspace.css';
import '../styles/distributor-overview.css';

const metrics = [
  ['Customers', 'customers', 'home_health', '/admin/distributor-customers'],
  ['Orders', 'orders', 'order_approve', '/admin/distributor-orders'],
  ['IVRs', 'ivrs', 'call_quality', '/admin/distributor-ivrs'],
  ['Shipments', 'shipments', 'package_2', '/admin/distributor-shipments'],
];
const validPage = (value) =>
  Array.isArray(value?.data) &&
  value.data.length <= 20 &&
  Number.isSafeInteger(value.total) &&
  value.total >= value.data.length;
const validOrders = (value) =>
  validPage(value) &&
  new Set(value.data.map((row) => row?._id)).size === value.data.length &&
  value.data.every(
    (row) =>
      typeof row?._id === 'string' &&
      typeof row.orderId === 'string' &&
      ['submitted', 'approved', 'shipped', 'completed', 'cancelled'].includes(row.status) &&
      typeof row.createdAt === 'string' &&
      Number.isFinite(Date.parse(row.createdAt))
  );

export default function AdminDistributorOverview() {
  const t = useT();
  const locale = useLocale();
  const { begin, cancel, version } = useShippingRequest('admin-distributor-overview');
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const data = result?.version === version ? result.data : null;
  useEffect(() => {
    const request = begin();
    setResult({ version, data: Array(4) });
    const reads = [
      () => distributorService.adminCustomerOverview({ page: 1, limit: 20 }),
      () => distributorService.adminActivityOverview('orders'),
      () => distributorService.adminActivityOverview('ivrs'),
      () => distributorService.adminActivityOverview('shipments'),
    ];
    reads.forEach((read, index) => {
      Promise.resolve()
        .then(read)
        .then((response) => {
          const value = response?.data?.data;
          return (index === 1 ? validOrders(value) : validPage(value)) ? value : null;
        })
        .catch(() => null)
        .then((value) => {
          if (!request.isCurrent()) return;
          setResult((current) => ({
            version,
            data: Array.from({ length: 4 }, (_, key) =>
              key === index ? value : current?.data[key]
            ),
          }));
        });
    });
    return cancel;
  }, [begin, cancel, version, refresh]);
  const orders = data?.[1]?.data.slice(0, 5);
  return (
    <section className="me-dw me-overview" aria-label={t('Distributor overview')}>
      <PageHeader
        title="Overview"
        subtitle="All distributors. Direct MedEffects records are separate."
      >
        <button type="button" className="me-dw-link" onClick={() => setRefresh((n) => n + 1)}>
          {t('Refresh overview')}
        </button>
      </PageHeader>
      <div className="me-overview-summary">
        <section className="me-overview-bank" aria-label={t('Bank summary')}>
          <header className="me-overview-panel-heading">
            <h2>{t('Virtual Tissue Bank Balance')}</h2>
            <span className="material-symbols-outlined" aria-hidden="true">
              account_balance_wallet
            </span>
          </header>
          <div className="me-overview-bank-body">
            <p className="me-overview-muted">{t('USD credit by distributor')}</p>
            <p className="me-overview-legacy">
              {t('Choose a distributor to review its Virtual Tissue Bank Balance.')}
            </p>
            <Link to="/admin/distributor-bank" className="me-overview-bank-link">
              {t('Open tissue bank')} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </section>
        <div className="me-overview-metrics">
          {metrics.map(([label, key, icon, to], index) => (
            <Link key={key} to={to} className="me-overview-metric">
              <div>
                <span className="me-overview-metric-label">{t(label)}</span>
                <strong>
                  {data?.[index] === undefined
                    ? '…'
                    : data[index]
                      ? data[index].total.toLocaleString(locale)
                      : '—'}
                </strong>
                {data?.[index] === null && (
                  <small role="status">{t('Unavailable. Refresh to try again.')}</small>
                )}
              </div>
              <span className="material-symbols-outlined" aria-hidden="true">
                {icon}
              </span>
            </Link>
          ))}
        </div>
      </div>
      <section className="me-overview-panel" aria-label={t('Recent orders')}>
        <header className="me-overview-panel-heading">
          <h2>{t('Recent orders')}</h2>
          <Link to="/admin/distributor-orders" className="me-dw-link">
            {t('View all orders')} <span aria-hidden="true">→</span>
          </Link>
        </header>
        {data?.[1] === undefined ? (
          <p role="status" className="me-overview-empty">
            {t('Loading overview...')}
          </p>
        ) : !orders ? (
          <p role="status" className="me-overview-empty">
            {t('Recent orders are unavailable. Refresh to try again.')}
          </p>
        ) : orders.length === 0 ? (
          <p className="me-overview-empty">{t('No orders yet.')}</p>
        ) : (
          <div
            className="me-overview-table-scroll"
            tabIndex={0}
            role="region"
            aria-label={t('Recent orders table')}
          >
            <table>
              <thead>
                <tr>
                  {['Order', 'Customer', 'Status', 'Submitted'].map((label) => (
                    <th key={label} scope="col">
                      {t(label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>{order.orderId}</td>
                    <td>{order.clinic?.name || '—'}</td>
                    <td>
                      <StatusBadge status={order.status} size="small" />
                    </td>
                    <td className="me-overview-date">
                      {new Date(order.createdAt).toLocaleDateString(locale, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
